/**
 * Icon Alias Manager for jsPDF Optimization
 * 
 * Implements icon caching to dramatically reduce PDF generation time
 * by pre-generating common icons once and reusing the PNG data.
 * 
 * Performance Impact:
 * - WITHOUT caching: 365 days × 8 icons/day = 2,920+ icon generations
 * - WITH caching: ~20 unique icons generated once = 99% reduction
 * 
 * Usage:
 * ```javascript
 * const aliasManager = new IconAliasManager(pdf, themeColors);
 * await aliasManager.initializeAliases();
 * 
 * // Use cached icon
 * aliasManager.addToPage('droplet-20', x, y, 20, 20);
 * 
 * // Add new icon type
 * await aliasManager.registerIcon('new-icon', color, 24);
 * ```
 */

import { getIcon } from './iconLibrary';
//import { IconAliasManager } from './iconAliasManager';




/**
 * Icon Alias Manager Class
 * Handles pre-generation and reuse of common PDF icons
 */
export class IconAliasManager {
  constructor(pdf, themeColors, iconSizes = null) {
    this.pdf = pdf;
    this.themeColors = themeColors;
    this.iconCache = new Map(); // Stores PNG data URLs instead of aliases
    this.registeredIcons = new Set();
    
    // Use configurable icon sizes or fallback to defaults
    this.iconSizes = iconSizes || {
      coverIcon: 600,
      dailyIcons: 64
    };
    
    console.log(`🎨 IconAliasManager initialized with icon sizes:`, this.iconSizes);
  }

  /**
   * PHASE 1: Pre-generate all common icons used across daily pages
   * This runs ONCE at PDF initialization
   */
  async initializeAliases() {
    console.log('🎨 Initializing icon cache for PDF optimization...');
    const startTime = Date.now();

    // Get common colors
    const primaryColor = this.themeColors?.primary;
    const secondaryColor = this.themeColors?.secondary;
    const accentColor = this.themeColors?.accent;

    // Get configured icon sizes
    const dailyIconSize = this.iconSizes?.dailyIcons;
    const coverIconSize = this.iconSizes?.coverIcon;

    // Define all common icon configurations using configurable sizes
    // Format: { name, size, color, bgColor (optional), usage }
    const commonIcons = [
      // Navigation icons (configurable size for page footers/headers)
      { name: 'arrow-left-circle', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'navigation' },
      { name: 'arrow-right-circle', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'navigation' },
      
      // Page type icons (configurable size for page headers)
      { name: 'book-open', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'journal-link' },
      { name: 'calendar', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'schedule-link' },
      { name: 'clipboard-check', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'tasks-link' },
      { name: 'user', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'lifehack-link' },
      { name: 'target', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'habit-link' },
      { name: 'clock', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'schedule-only-link' },
      { name: 'menu', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'index-link' },
      
      
      // Daily page tracker icons (configurable size)
      { name: 'droplet', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'water-tracker' },
      { name: 'glass-water', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'water-tracker' },
      
      // Weather icons - Temperature group (configurable size)
      { name: 'thermometer', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-temperature' },
      { name: 'thermometer-sun', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-hot' },
      { name: 'thermometer-snowflake', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-cold' },
      
      // Weather icons - Sunshine group (configurable size)
      { name: 'sun', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-sunny' },
      { name: 'sun-dim', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-dim' },
      { name: 'moon', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-night' },
      
      // Weather icons - Precipitation group (configurable size)
      { name: 'cloud', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-cloudy' },
      { name: 'cloud-rain', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-rain' },
      { name: 'cloud-drizzle', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-drizzle' },
      { name: 'cloud-snow', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-snow' },
      { name: 'snowflake', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-snowflake' },
      { name: 'zap', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'weather-storm' },
      
      // Mood tracker icons (configurable size)
      { name: 'smile', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'mood-happy' },
      { name: 'meh', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'mood-neutral' },
      { name: 'frown', size: dailyIconSize, color: primaryColor, bgColor: accentColor, usage: 'mood-sad' },
      
      // Cover page watermark (configurable large size)
      { name: 'calendar', size: coverIconSize, color: secondaryColor, bgColor: primaryColor, usage: 'cover-watermark' }
    ];

    // Generate and cache all common icons
    for (const iconConfig of commonIcons) {
      await this.registerIcon(
        iconConfig?.name,
        iconConfig?.color,
        iconConfig?.size,
        iconConfig?.bgColor,
        iconConfig?.usage
      );
    }

    const endTime = Date.now();
    const duration = endTime - startTime;
    
    console.log(`✅ Icon caching complete:`);
    console.log(`   - Cached ${this.iconCache?.size} unique icon variations`);
    console.log(`   - Generation time: ${duration}ms`);
    console.log(`   - Estimated speedup: 80-90% for icon-heavy pages`);
    console.log(`   - Cache keys: ${Array.from(this.iconCache?.keys())?.join(', ')}`);
  }

  /**
   * Register a single icon and cache its PNG data
   * @param {string} iconName - Icon name from iconLibrary
   * @param {Object} color - RGB color object {r, g, b}
   * @param {number} size - Icon size in pixels
   * @param {Object} bgColor - Optional background color
   * @param {string} usage - Optional usage description for debugging
   * @returns {Promise<string>} The cache key for this icon
   */
  async registerIcon(iconName, color, size, bgColor = null, usage = null) {
    // Create unique cache key based on icon properties
    const cacheKey = this.createCacheKey(iconName, size, color, bgColor);

    // Skip if already cached
    if (this.iconCache?.has(cacheKey)) {
      return cacheKey;
    }

    try {
      // Generate icon PNG once
      const iconPng = await getIcon(iconName, color, size, bgColor);

      // Validate PNG data URL format
      if (!iconPng || !iconPng?.startsWith('data:image/png;base64,')) {
        throw new Error(`Invalid PNG data URL format for icon: ${iconName}`);
      }

      // Extract base64 data without the data URL prefix for validation
      const base64Data = iconPng?.split(',')?.[1];
      if (!base64Data || base64Data?.length === 0) {
        throw new Error(`Empty PNG data for icon: ${iconName}`);
      }

      // Store PNG data URL in cache for reuse
      this.iconCache?.set(cacheKey, {
        pngData: iconPng,
        iconName,
        size,
        color,
        bgColor,
        usage,
        registered: true
      });

      this.registeredIcons?.add(iconName);

      return cacheKey;
    } catch (error) {
      console.error(`❌ Failed to cache icon: ${cacheKey}`, error);
      console.error(`   Icon: ${iconName}, Size: ${size}, Usage: ${usage || 'N/A'}`);
      throw new Error(`Icon caching failed for ${iconName}: ${error.message}`);
    }
  }

  /**
   * PHASE 2: Use cached icon on page (instant reuse)
   * @param {string} cacheKey - The cache key returned from registerIcon
   * @param {number} x - X position on page
   * @param {number} y - Y position on page
   * @param {number} width - Display width in mm
   * @param {number} height - Display height in mm
   */
  addToPage(cacheKey, x, y, width, height) {
    const cachedIcon = this.iconCache?.get(cacheKey);
    
    if (!cachedIcon) {
      //console.warn(`⚠️ Icon not found in cache: ${cacheKey}`);
      return false;
    }

    try {
      // Use cached PNG data - no generation needed, instant reuse
      this.pdf?.addImage(cachedIcon?.pngData, 'PNG', x, y, width, height);
      return true;
    } catch (error) {
      console.error(`❌ Failed to add cached icon to page: ${cacheKey}`, error);
      throw new Error(`Failed to add icon: ${error.message}`);
    }
  }

  /**
   * Helper method: Get cache key for specific icon configuration
   * Use this to retrieve the correct cache key when you know the icon properties
   */
  getCacheKey(iconName, size, color, bgColor = null) {
    return this.createCacheKey(iconName, size, color, bgColor);
  }

  /**
   * Helper method: Add icon to page using icon properties
   * Automatically looks up the correct cache key
   */
  addIconByProperties(iconName, size, color, bgColor, x, y, width, height) {
    const cacheKey = this.getCacheKey(iconName, size, color, bgColor);
    return this.addToPage(cacheKey, x, y, width, height);
  }

  /**
   * Create unique cache key from icon properties
   * Format: iconName-size-r-g-b[-bgr-bgg-bgb]
   */
  createCacheKey(iconName, size, color, bgColor = null) {
    const colorString = `${color?.r}-${color?.g}-${color?.b}`;
    const bgColorString = bgColor ? `-bg${bgColor?.r}-${bgColor?.g}-${bgColor?.b}` : '';
    return `${iconName}-${size}-${colorString}${bgColorString}`;
  }

  /**
   * Check if an icon is cached
   */
  isCached(cacheKey) {
    return this.iconCache?.has(cacheKey);
  }

  /**
   * Get all cached keys (for debugging)
   */
  getAllCachedKeys() {
    return Array.from(this.iconCache?.keys());
  }

  /**
   * Get statistics about cached icons
   */
  getStats() {
    return {
      totalCached: this.iconCache?.size,
      uniqueIcons: this.registeredIcons?.size,
      cachedIcons: Array.from(this.iconCache?.entries())?.map(([key, metadata]) => ({
        key,
        iconName: metadata?.iconName,
        size: metadata?.size,
        usage: metadata?.usage
      }))
    };
  }

  /**
   * EXTENSION POINT: Add new icon type to the cache
   * Use this when adding new features that need new icons
   * 
   * @example
   * // Add a new "bookmark" icon for a new feature
   * await aliasManager.addNewIconType('bookmark', 64, primaryColor, accentColor, 'bookmark-feature');
   */
  async addNewIconType(iconName, size, color, bgColor = null, usage = null) {
    console.log(`➕ Adding new icon type: ${iconName} (${usage || 'custom'})`);
    return await this.registerIcon(iconName, color, size, bgColor, usage);
  }

  /**
   * BATCH EXTENSION: Add multiple new icon types at once
   * Use this when adding a new feature with multiple icons
   * 
   * @example
   * await aliasManager.addNewIconTypes([
   *   { name: 'star', size: 64, color: primaryColor, usage: 'favorites' },
   *   { name: 'heart', size: 64, color: primaryColor, usage: 'likes' }
   * ]);
   */
  async addNewIconTypes(iconConfigs) {
    console.log(`➕ Adding ${iconConfigs?.length} new icon types...`);
    const results = [];
    
    for (const config of iconConfigs) {
      const cacheKey = await this.registerIcon(
        config?.name,
        config?.color,
        config?.size,
        config?.bgColor || null,
        config?.usage || null
      );
      results?.push(cacheKey);
    }
    
    return results;
  }
}

/**
 * CONVENIENCE FUNCTIONS for common use cases
 */

/**
 * Create a pre-configured alias manager for a planner PDF
 * This handles all the standard planner icons automatically
 * 
 * @param {Object} pdf - jsPDF instance
 * @param {Object} themeColors - Theme color configuration
 * @param {Object} iconSizes - Optional icon size configuration
 * @returns {Promise<IconAliasManager>} Initialized alias manager
 */
export async function createPlannerAliasManager(pdf, themeColors, iconSizes = null) {
  const manager = new IconAliasManager(pdf, themeColors, iconSizes);
  await manager?.initializeAliases();
  return manager;
}

/**
 * Get standard icon size mappings for planner
 * DEPRECATED: Use ICON_SIZES from pdfGenerator.js instead
 */
export const PLANNER_ICON_SIZES = {
  navigation: 64,      // Footer navigation arrows (DEPRECATED - now configurable)
  headerLink: 64,      // Header page type links (DEPRECATED - now configurable)
  waterTracker: 64,    // Water intake icons (DEPRECATED - now configurable)
  weather: 64,         // Weather icons (DEPRECATED - now configurable)
  mood: 64,            // Mood tracker icons (DEPRECATED - now configurable)
  coverWatermark: 600  // Large cover page calendar (DEPRECATED - now configurable)
};

export default IconAliasManager;